import { GlobalErrorView } from '@client/components/_core/error-handling/GlobalErrorView';
import { RouteChunkStallView } from '@client/components/_core/error-handling/RouteChunkStallView';
import { routeChunkStallCapture } from '@client/components/_core/error-handling/route-chunk-stall-capture';
import { useErrorRouteContext } from '@client/components/_core/error-handling/useErrorRouteContext';
import { useAuthStore } from '@client/stores/AuthStore';
import { isCaptureLeftToServer } from '@client/trpc/error-classification';
import { isAuthRoute } from '@client/utils/common/auth';
import { RouteChunkStallError } from '@client/utils/routing/route-chunk-stall';
import * as Sentry from '@sentry/react';
import type { ErrorComponentProps } from '@tanstack/react-router';
import { useEffect } from 'react';

export function RouterErrorComponent(props: ErrorComponentProps) {
    const { isPublicSection, orgId } = useErrorRouteContext();

    const { error, reset, info } = props;

    // Capture on error changes only, including errors hidden by the auth suppression below. A
    // refusal the server marked `clientCapture: false` is the server's to report (CUR-1962).
    useEffect(() => {
        if (error && !isCaptureLeftToServer(error)) {
            const stall = routeChunkStallCapture(error);
            Sentry.captureException(error, {
                tags: { location: 'ErrorFallback' },
                level: 'error',
                ...(stall && { fingerprint: stall.fingerprint }),
                extra: {
                    orgId,
                    isPublicSection,
                    url: window.location.href,
                    userAgent: navigator.userAgent,
                    info,
                    ...stall?.extra,
                },
            });
        }
    }, [error]);

    // Before the expired-token suppression below: a stalled route's code never arrived, so nothing on
    // the page will redirect to the login, and a blank page would leave no way out.
    if (error instanceof RouteChunkStallError) {
        return <RouteChunkStallView />;
    }

    // TODO: Remove this once we've migrated from Hasura
    //  Ignore errors on auth routes if the token is expired
    // There should be already a notification displayed
    // After migrating from Hasura, this error fallback is no longer needed
    if (useAuthStore.getState().authTokenExpired && isAuthRoute()) {
        return <div />;
    }

    return <GlobalErrorView isPublicSection={isPublicSection} orgId={orgId} error={error} reset={reset} />;
}
